import { SimpleGrid, Button, Text, Input, Flex, Box } from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'
import { StageTitle } from './StageTitle'
import { useSignInModal } from '../SigninHostModal'
import { StatusView } from './StatusView'
import { useMutation } from '@tanstack/react-query'
import { FormProvider, useController, useForm, useFormContext, useWatch } from 'react-hook-form'
import { inAppWallet, preAuthenticate } from 'thirdweb/wallets/in-app'
import { thirdwebClient } from '@/services/wallet-service'
import { tryCatchAsync } from '@/helpers/trycatch'
import { useTranslation } from 'next-i18next/pages'

export const EmailFlow = ({ email }: { email: string }) => {
  const { handleWalletFlow, navigateTo } = useSignInModal()

  const [isProcessing, setProcessing] = useState(false)
  const [isOtpValidating, setOtpValidating] = useState(true)

  useEffect(() => {
    setProcessing(false)
    handleSendOtp()
  }, [])

  const handleSendOtp = async () => {
    const result = await tryCatchAsync(
      preAuthenticate({
        client: thirdwebClient,
        strategy: 'email',
        email: email,
      })
    )
    if (result.error) {
      console.error('Error pre-authenticating:', result.error)
      setOtpValidating(false)
    }
    setOtpValidating(false)
  }

  const methods = useForm()

  if (isProcessing) {
    return (
      <StatusView
        error={false}
        isSuccess={false}
        backCallback={() => null}
        retryCallback={() => null}
        provider={'email'}
      />
    )
  }

  const handleVerifyCode = async (otp: string) => {
    setOtpValidating(true)
    const wallet = inAppWallet()
    const account = await tryCatchAsync(
      wallet.connect({
        client: thirdwebClient,
        strategy: 'email',
        email: email,
        verificationCode: otp,
      })
    )

    if (account.error) {
      console.error('Error connecting wallet:', account.error)
      setOtpValidating(false)
      methods.setError('otp', { type: 'manual', message: 'Invalid OTP' })
      return
    }
    methods.clearErrors('otp')

    if (account.data) {
      handleWalletFlow('email', account.data.address)
    }

    return null
  }

  return (
    <FormProvider {...methods}>
      <Otp
        isOtpValidating={isOtpValidating}
        handleVerifyCode={handleVerifyCode}
        handleBack={() => {
          navigateTo('SignIn')
        }}
        email={email}
      />
    </FormProvider>
  )
}

export const Otp = ({ handleVerifyCode, isOtpValidating, handleBack, email }: any) => {
  const { mutate, isPending } = useMutation({
    mutationFn: async (otp: string) => {
      await handleVerifyCode(otp)
    },
  })

  const { register } = useFormContext()
  const otp = useWatch({ name: 'otp' })
  const { t } = useTranslation()

  return (
    <>
      <Flex flexDir={'column'} h={'full'} justifyContent={'space-between'}>
        <StageTitle title={t('signin.sign-up-with-email')} backCallback={handleBack} />
        <Box>
          <Text color={'ninja.200'} fontSize={'sm'} textAlign={'center'} fontWeight={500} mb={3}>
            {t('signin.enter-code-sent-to', { email })}
          </Text>
          <SimpleGrid columns={6} gap={4}>
            <OtpInputWithValidation {...register('otp')} numberOfDigits={6} verifyCode={mutate} isBlocked={isPending} />
          </SimpleGrid>
        </Box>
        <Button
          onClick={() => {
            mutate(otp)
          }}
          isDisabled={otp?.length != 6}
          isLoading={isOtpValidating}
          rounded={'xl'}
          w={'full'}
          h={12}
          mb={4}
        >
          {t('common.button.sign-in')}
        </Button>
      </Flex>
    </>
  )
}

const OtpInputWithValidation = ({
  numberOfDigits,
  isBlocked,
  verifyCode,
  ...props
}: {
  numberOfDigits: number
  isBlocked: boolean
  verifyCode: (code: string) => void
  name: string
}) => {
  const otpBoxReference = useRef<Array<HTMLInputElement>>([])
  const [isFirst, setFirst] = useState(true)
  const { clearErrors } = useFormContext()
  const {
    field,
    fieldState: { invalid },
  } = useController(props as any)

  const [otp, setOtp] = useState<string[]>(Array.from({ length: numberOfDigits }, () => ''))

  function handleChange(value: string, index: number) {
    let newArr = [...otp]
    newArr[index] = value
    setOtp(newArr)
    field.onChange(newArr.join(''))

    if (value && index < numberOfDigits - 1) {
      otpBoxReference.current[index + 1].focus()
    }
  }

  function handleBackspaceAndEnter(e: any, index: number) {
    if (e.key === 'Backspace' && !e.target.value && index > 0) {
      otpBoxReference.current[index - 1].focus()
    }
  }

  useEffect(() => {
    otpBoxReference.current.forEach((element) => {
      element.addEventListener('paste', (event) => {
        event.preventDefault()
        let paste = event.clipboardData?.getData('text')
        setOtp(paste?.split('') || [])
      })
    })
  }, [])

  useEffect(() => {
    field.onChange(otp.join(''))
    if (otp.every((number) => number !== '') && otp.length === numberOfDigits) {
      clearErrors(props.name)

      if (isFirst) {
        verifyCode(otp.join(''))
      }
      setFirst(false)
    } else {
    }
  }, [otp])

  useEffect(() => {
    if (!field.value) {
      setOtp(Array.from({ length: numberOfDigits }, () => ''))
    }
  }, [field.value])

  return (
    <>
      {Array.from({ length: numberOfDigits }).map((_, index) => (
        <Input
          isDisabled={isBlocked}
          key={index}
          value={otp[index] || ''}
          maxLength={1}
          onClick={() => {
            clearErrors(props.name)
          }}
          onChange={(e) => {
            clearErrors(props.name)
            if (e.target.value !== '' && isNaN(parseInt(e.target.value))) {
              e.preventDefault()
              return
            }
            handleChange(e.target.value, index)
          }}
          onKeyDown={(e) => handleBackspaceAndEnter(e, index)}
          ref={(reference) => (otpBoxReference.current[index] = reference!) as any}
          mb={4}
          h={'50px'}
          border={'2px solid'}
          borderColor={invalid ? 'brand.error !important' : 'transparent !important'}
          bg={'ninja.500'}
          rounded={'xl'}
          outline={'none'}
          textAlign={'center'}
          _focusVisible={{
            outline: 'none',
            borderColor: invalid ? 'brand.error !important' : 'transparent !important',
          }}
          _active={{
            outline: 'none',
            borderColor: invalid ? 'brand.error !important' : 'transparent !important',
          }}
          _hover={{
            borderColor: 'auto',
          }}
          color={'brand.white'}
          _placeholder={{
            color: 'ninja.300',
          }}
          fontWeight={700}
          fontSize={'sm'}
        />
      ))}
    </>
  )
}
